import { useEffect, useState } from "react";

import { reverse } from "@reactivated";

import {
    LatLng,
    LocationQueryResponse,
    Meters,
    check,
    isoLatitude,
    isoLongitude,
    isoMeters,
} from "../models";

type UserLocation = {
    detected: LatLng;
    entered: LatLng | null;
    bounds?: google.maps.LatLngBounds;
};

type LocationQuery = {
    center: LatLng;
    radius: Meters;
    limit: number;
    locationType: string;
};

export const QUERY_RADIUS = isoMeters.wrap(100_000);
const QUERY_LIMIT = 25;

const DEFAULT_LOCATION: UserLocation = {
    detected: {
        lat: isoLatitude.wrap(40.889),
        lng: isoLongitude.wrap(-73.941),
    },
    entered: null,
};

const DEFAULT_CANADA_LOCATION: UserLocation = {
    detected: {
        lat: isoLatitude.wrap(43.718),
        lng: isoLongitude.wrap(-79.543),
    },
    entered: null,
};

/**
 * Use the google maps API to geocode text into coordinates
 */
export const geocode = async (
    request: google.maps.GeocoderRequest,
): Promise<google.maps.GeocoderResult[]> => {
    const geocoder = new google.maps.Geocoder();
    const resp = await geocoder.geocode(request);
    return resp.results;
};

/**
 * Query the retail location API for the locations within the circle defined by
 * the center and radius parameters.
 */
const listLocations = async (
    query: LocationQuery,
): Promise<LocationQueryResponse> => {
    const url = reverse("cms-api:list-locations");
    const querystring = new URLSearchParams({
        lat: `${isoLatitude.unwrap(query.center.lat)}`,
        lng: `${isoLongitude.unwrap(query.center.lng)}`,
        radius: `${Math.round(isoMeters.unwrap(query.radius))}`,
        limit: `${query.limit}`,
        location_type: query.locationType,
    });
    const response = await fetch(`${url}?${querystring.toString()}`);
    const data: unknown = await response.json();
    return check(LocationQueryResponse.decode(data));
};

/**
 * React hook for getting the user's current geolocation, and allowing them to
 * optionally override the detected location.
 */
export const useUserLocation = (mapType: string) => {
    const [userLocation, setUserLocation] = useState<UserLocation>(
        // A little hacky but services are a CA thing and we need something
        // to actually show up when the service center map loads
        mapType === "DEALER" ? DEFAULT_LOCATION : DEFAULT_CANADA_LOCATION,
    );

    // Find the user's location using the navigator.geolocation API
    const getBrowserGeoLocation = () => {
        const onSuccess: PositionCallback = (pos) => {
            setUserLocation((s) => ({
                ...s,
                detected: {
                    lat: isoLatitude.wrap(pos.coords.latitude),
                    lng: isoLongitude.wrap(pos.coords.longitude),
                },
            }));
        };
        const onError: PositionErrorCallback = (err) => {
            console.warn(err);
        };
        const options: PositionOptions = {
            enableHighAccuracy: false,
        };
        navigator.geolocation.getCurrentPosition(onSuccess, onError, options);
    };

    useEffect(() => {
        getBrowserGeoLocation();
    }, []);

    const setEnteredLocation = (
        enteredLocation: LatLng | null,
        bounds?: google.maps.LatLngBounds,
    ): void => {
        setUserLocation((s) => ({
            ...s,
            entered: enteredLocation,
            bounds,
        }));
    };

    return {
        userLocation,
        setEnteredLocation,
    };
};

export const getBoundsRadius = (bounds: google.maps.LatLngBounds) => {
    const radiusInMeters =
        google.maps.geometry.spherical.computeDistanceBetween(
            bounds.getCenter(),
            bounds.getNorthEast(),
        );

    return radiusInMeters > isoMeters.unwrap(QUERY_RADIUS)
        ? isoMeters.wrap(Math.round(radiusInMeters))
        : QUERY_RADIUS;
};

/**
 * React hook for listing the nearby locations for the given user location
 */
export const useNearbyRetailLocations = (
    userLocation: UserLocation,
    mapType: string,
    radiusOverride?: Meters,
) => {
    const [queryResp, setQueryResp] = useState<LocationQueryResponse | null>(
        null,
    );

    const radius = radiusOverride
        ? radiusOverride
        : userLocation.bounds
          ? getBoundsRadius(userLocation.bounds)
          : QUERY_RADIUS;

    useEffect(() => {
        let stale = false;
        const doFetch = async () => {
            const center = userLocation.entered || userLocation.detected;
            const resp = await listLocations({
                center,
                radius,
                limit: QUERY_LIMIT,
                locationType: mapType,
            });
            if (!stale) {
                setQueryResp(resp);
            }
        };
        doFetch().catch((err) => {
            if (!stale) {
                console.error(err);
            }
        });
        return () => {
            stale = true;
        };
    }, [userLocation, radius, mapType]);

    return queryResp;
};
